const STAGE_WIDTH = 3840;
const STAGE_HEIGHT = 2160;
const CAMERA_END_X = 1920;
const CAMERA_END_Y = 1080;

class BwsExplodedView {
  #root;
  #cam;
  #stage;
  #nodes = [];
  #assembledW = 0;
  #assembledH = 0;
  #fx0 = CAMERA_END_X;
  #fy0 = CAMERA_END_Y;
  #k = 1;
  #z0 = 1;
  #baseWidth = 0;
  #viewport;
  #progress = 0;
  #resizeObserver;

  constructor(root) {
    this.#root = root;
    this.#cam = root.querySelector('[data-bws-exploded-cam]');
    this.#stage = root.querySelector('[data-bws-exploded-stage]');
    this.#assembledW = Number(root.dataset.assembledW);
    this.#assembledH = Number(root.dataset.assembledH);
    this.#fx0 = Number(root.dataset.fx0);
    this.#fy0 = Number(root.dataset.fy0);
    this.#viewport = root.parentElement;
    this.#nodes = [...(this.#stage?.querySelectorAll('.bws-performance__exploded-part') ?? [])].map((img) => {
      const width = Number(img.dataset.w);
      const height = Number(img.dataset.h);
      const scaleEnd = Number(img.dataset.s1);
      const id = img.dataset.id ?? '';

      return {
        img,
        width,
        height,
        x0: Number(img.dataset.x0),
        y0: Number(img.dataset.y0),
        s0: Number(img.dataset.s0),
        x1: Number(img.dataset.x1),
        y1: Number(img.dataset.y1),
        s1: scaleEnd,
        delay: Number(img.dataset.d),
        span: Number(img.dataset.sp) || 1,
        fadeIn: parseRange(img.dataset.fin),
        fadeOut: parseRange(img.dataset.fout),
        bcx: Number(img.dataset.x1) + (width * scaleEnd) / 2,
        bcy: Number(img.dataset.y1) + (height * scaleEnd) / 2,
        spin: id.startsWith('screw') ? (id.charCodeAt(5) % 2 ? -1 : 1) : 0,
      };
    });

    this.measure();
    this.#resizeObserver = new ResizeObserver(() => this.measure());
    this.#resizeObserver.observe(this.#viewport || this.#root);

    for (const node of this.#nodes) {
      if (!node.img.complete) {
        node.img.addEventListener('load', () => this.measure(), { once: true });
      }
    }
  }

  disconnect() {
    this.#resizeObserver?.disconnect();
  }

  measure() {
    if (!this.#stage || !this.#root) return;

    const viewport = this.#viewport || this.#root;
    this.#baseWidth = viewport.clientWidth;
    const baseHeight = this.#baseWidth * (STAGE_HEIGHT / STAGE_WIDTH);
    const viewHeight = viewport.clientHeight || window.innerHeight;
    const viewWidth = this.#baseWidth || window.innerWidth;
    const driverHeight = (this.#assembledH / STAGE_HEIGHT) * baseHeight;
    const driverWidth = (this.#assembledW / STAGE_WIDTH) * this.#baseWidth;
    this.#z0 = Math.max(
      1,
      Math.min((0.5 * viewHeight) / Math.max(driverHeight, 1), (0.4 * viewWidth) / Math.max(driverWidth, 1)),
    );
    this.paint(this.#progress);
  }

  paint(progress) {
    if (!this.#cam || !this.#stage) return;

    const p = clamp(progress);
    this.#progress = p;
    const cameraT = ease(clamp(p / 0.78));
    const zoom = this.#z0 + (1 - this.#z0) * cameraT;

    this.#cam.style.width = `${(zoom * 100).toFixed(4)}%`;
    this.#k = this.#stage.clientWidth / STAGE_WIDTH;

    for (const node of this.#nodes) {
      const t = ease(clamp((p - node.delay) / node.span));
      const scale = node.s0 + (node.s1 - node.s0) * t;
      const cx = node.x0 + (node.x1 - node.x0) * t + (node.width * scale) / 2;
      const cy = node.y0 + (node.y1 - node.y0) * t + (node.height * scale) / 2;
      let opacity = 1;

      if (node.fadeIn) opacity *= clamp((p - node.fadeIn[0]) / (node.fadeIn[1] - node.fadeIn[0]));
      if (node.fadeOut) opacity *= 1 - clamp((p - node.fadeOut[0]) / (node.fadeOut[1] - node.fadeOut[0]));

      const rotation = node.spin ? node.spin * (1 - t) * 540 : 0;
      node.img.style.transform =
        `translate(${((cx - node.bcx) * this.#k).toFixed(2)}px,${((cy - node.bcy) * this.#k).toFixed(2)}px)` +
        ` scale(${(scale / node.s1).toFixed(4)})` +
        (rotation ? ` rotate(${rotation.toFixed(1)}deg)` : '');
      node.img.style.opacity = opacity.toFixed(3);
      node.img.style.visibility = opacity < 0.004 ? 'hidden' : 'visible';
    }

    const focusX = this.#fx0 + (CAMERA_END_X - this.#fx0) * cameraT;
    const focusY = this.#fy0 + (CAMERA_END_Y - this.#fy0) * cameraT;
    const stageWidth = this.#stage.clientWidth;
    const stageHeight = this.#stage.clientHeight;

    this.#cam.style.transform = `translate(${((-(focusX - CAMERA_END_X) / STAGE_WIDTH) * stageWidth).toFixed(
      2,
    )}px,${((-(focusY - CAMERA_END_Y) / STAGE_HEIGHT) * stageHeight).toFixed(2)}px)`;
  }
}

class BwsPerformanceTransition extends HTMLElement {
  #controller;
  #frame;
  #stage;
  #video;
  #toggleButton;
  #explodedView;
  #reducedMotion;
  #intersectionObserver;
  #progress = 0;
  #targetProgress = 0;
  #direction = 1;
  #hovered = false;
  #dragging = false;
  #visible = false;
  #paused = false;
  #lastPointerY = 0;
  #lastTimestamp = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#stage = this.querySelector('[data-bws-performance-stage]');
    this.#video = this.querySelector('video');
    this.#toggleButton = this.querySelector('[data-bws-performance-toggle]');
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const explodedRoot = this.querySelector('[data-bws-exploded]');
    this.#explodedView = explodedRoot ? new BwsExplodedView(explodedRoot) : undefined;

    if (this.#reducedMotion.matches) {
      if (this.#toggleButton) this.#toggleButton.hidden = true;
      this.style.setProperty('--bws-performance-progress', '1');
      this.#explodedView?.paint(1);
      return;
    }

    const { signal } = this.#controller;
    window.addEventListener('resize', this.#requestUpdate, { passive: true, signal });
    this.#video?.addEventListener('loadedmetadata', this.#requestUpdate, { signal });
    this.#toggleButton?.addEventListener('click', this.#handleToggle, { signal });
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.#stage?.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
      this.#stage?.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
      this.#stage?.addEventListener('pointerdown', this.#handlePointerDown, { signal });
      this.#stage?.addEventListener('pointermove', this.#handlePointerMove, { signal });
      this.#stage?.addEventListener('pointerup', this.#handlePointerUp, { signal });
      this.#stage?.addEventListener('pointercancel', this.#handlePointerUp, { signal });
      this.#stage?.addEventListener('wheel', this.#handleWheel, { passive: false, signal });
    }

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#visible = entry.isIntersecting;
        this.#lastTimestamp = 0;
        if (this.#visible) this.#requestUpdate();
      },
      { threshold: 0.15 },
    );
    this.#intersectionObserver.observe(this);
    this.#requestUpdate();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#intersectionObserver?.disconnect();
    this.#explodedView?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #requestUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(this.#tick);
  };

  #handleToggle = () => {
    this.#paused = !this.#paused;
    this.#lastTimestamp = 0;

    if (this.#toggleButton) {
      this.#toggleButton.dataset.paused = String(this.#paused);
      this.#toggleButton.setAttribute(
        'aria-label',
        this.#paused ? this.#toggleButton.dataset.playLabel : this.#toggleButton.dataset.pauseLabel,
      );
    }

    if (!this.#paused) this.#requestUpdate();
  };

  #handlePointerEnter = () => {
    this.#hovered = true;
    this.#lastTimestamp = 0;
  };

  #handlePointerLeave = () => {
    if (this.#dragging) return;
    this.#hovered = false;
    this.#lastTimestamp = 0;
    this.#requestUpdate();
  };

  #handlePointerDown = (event) => {
    if (event.button !== 0 || !this.#stage) return;

    this.#dragging = true;
    this.#lastPointerY = event.clientY;
    this.#stage.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  #handlePointerMove = (event) => {
    if (!this.#dragging || !this.#stage) return;

    const delta = event.clientY - this.#lastPointerY;
    this.#lastPointerY = event.clientY;
    this.#setInteractiveProgress(this.#targetProgress + delta / Math.max(this.#stage.clientHeight * 0.75, 1));
  };

  #handlePointerUp = (event) => {
    if (!this.#dragging) return;

    this.#dragging = false;
    if (this.#stage?.hasPointerCapture(event.pointerId)) this.#stage.releasePointerCapture(event.pointerId);
    this.#hovered = this.#stage?.matches(':hover') ?? false;
    this.#lastTimestamp = 0;
    if (!this.#hovered) this.#requestUpdate();
  };

  #handleWheel = (event) => {
    if (!this.#hovered || event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

    event.preventDefault();
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
    this.#setInteractiveProgress(this.#targetProgress + (event.deltaY * unit) / 1200);
  };

  #setInteractiveProgress(progress) {
    this.#targetProgress = clamp(progress);
    this.#progress = this.#targetProgress;
    this.#direction = this.#progress >= 1 ? -1 : this.#progress <= 0 ? 1 : this.#direction;
    this.#paint();
  }

  #tick = (timestamp) => {
    this.#frame = undefined;
    const busy = this.#update(timestamp);
    if (busy) this.#frame = requestAnimationFrame(this.#tick);
  };

  #update(timestamp) {
    const autoplaying = this.#visible && !this.#hovered && !this.#dragging && !this.#paused;
    if (autoplaying && this.#lastTimestamp) {
      this.#targetProgress += ((timestamp - this.#lastTimestamp) / 9000) * this.#direction;
      if (this.#targetProgress >= 1) {
        this.#targetProgress = 1;
        this.#direction = -1;
      } else if (this.#targetProgress <= 0) {
        this.#targetProgress = 0;
        this.#direction = 1;
      }
    }
    this.#lastTimestamp = timestamp;

    const delta = this.#targetProgress - this.#progress;
    if (Math.abs(delta) < 0.0008) {
      this.#progress = this.#targetProgress;
    } else {
      this.#progress += delta * 0.08;
    }

    this.#paint();

    return autoplaying || Math.abs(this.#progress - this.#targetProgress) >= 0.0008;
  }

  #paint() {
    this.style.setProperty('--bws-performance-progress', this.#progress.toFixed(4));
    this.#explodedView?.paint(this.#progress);

    if (this.#video && Number.isFinite(this.#video.duration) && this.#video.duration > 0) {
      const targetTime = Math.min(this.#video.duration - 0.01, this.#video.duration * this.#progress);
      if (Math.abs(this.#video.currentTime - targetTime) > 0.025) this.#video.currentTime = targetTime;
    }
  }
}

function parseRange(value) {
  if (!value) return null;

  const parts = value.split(',').map(Number);
  if (parts.length < 2 || parts.some((number) => !Number.isFinite(number))) return null;

  return parts;
}

function clamp(value) {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function ease(value) {
  return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
}

if (!customElements.get('bws-performance-transition')) {
  customElements.define('bws-performance-transition', BwsPerformanceTransition);
}
